import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, Heart, Leaf, Shield } from 'lucide-react';

const AboutPage = () => {
  const { t, language } = useLanguage();

  const values = language === 'en' ? [
    {
      icon: Heart,
      title: 'Serenity',
      description: 'We create spaces that calm the mind and nurture the soul.',
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Our organizing systems are built to last and evolve with your life.',
    },
    {
      icon: Shield,
      title: 'Trust',
      description: 'We treat your home and belongings with the utmost respect and care.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Certified expertise combined with personalized attention to detail.',
    },
  ] : [
    {
      icon: Heart,
      title: 'Serenidad',
      description: 'Creamos espacios que calman la mente y nutren el alma.',
    },
    {
      icon: Leaf,
      title: 'Sostenibilidad',
      description: 'Nuestros sistemas de organización están construidos para durar y evolucionar con tu vida.',
    },
    {
      icon: Shield,
      title: 'Confianza',
      description: 'Tratamos tu hogar y pertenencias con el máximo respeto y cuidado.',
    },
    {
      icon: Award,
      title: 'Excelencia',
      description: 'Experiencia certificada combinada con atención personalizada al detalle.',
    },
  ];

  return (
    <main data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <span className="font-accent text-lg text-[#8DA399]">
                {t('about.subtitle')}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C3333] mt-4 leading-tight">
                {t('about.title')}
              </h1>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/b4wk4gxj_IMG_2646.jpeg"
                alt="Galene Home - Organized serene space"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="lg:order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/7qaarhdv_IMG_4794.jpeg"
                alt="Peaceful organized home"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="lg:order-1">
              <h2 className="font-serif text-3xl md:text-4xl text-[#2C3333]">
                {language === 'en' ? 'Our Story' : 'Nuestra Historia'}
              </h2>
              <p className="mt-6 font-sans text-lg text-[#5C5C5C] leading-relaxed">
                {t('about.story')}
              </p>
              <p className="mt-6 font-sans text-lg text-[#5C5C5C] leading-relaxed">
                {t('about.mission')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C3333]">
              {t('about.philosophy')}
            </h2>
            <p className="mt-6 font-sans text-lg text-[#5C5C5C] max-w-2xl mx-auto">
              {t('about.philosophyText')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 text-center group hover:shadow-lg transition-shadow duration-300"
              >
                <div className="inline-flex p-4 bg-[#8DA399]/10 group-hover:bg-[#8DA399]/20 transition-colors rounded-full mb-6">
                  <value.icon className="w-8 h-8 text-[#8DA399]" />
                </div>
                <h3 className="font-serif text-xl text-[#2C3333]">{value.title}</h3>
                <p className="mt-4 font-sans text-[#5C5C5C]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C3333]">
              {t('about.certifications')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div
              data-testid="cert-home-academy"
              className="bg-white border border-[#E5E5E0] p-8 md:p-12 text-center hover:border-[#8DA399]/50 transition-colors col-span-1 md:col-span-2 max-w-xl mx-auto"
            >
              <div className="inline-flex p-4 bg-[#8DA399] rounded-full mb-6">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-serif text-xl text-[#2C3333]">The Home Academy</h3>
              <p className="mt-4 font-sans text-[#5C5C5C]">
                {t('about.certHome')}
              </p>
              <p className="mt-3 font-sans text-sm text-[#8DA399]">
                {t('about.certNapo')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-24 md:py-32 bg-[#2C3333]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white">
            {language === 'en' ? 'Follow Our Journey' : 'Sigue Nuestro Viaje'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/70">
            {language === 'en'
              ? 'Get inspired and see our latest projects on Instagram.'
              : 'Inspírate y mira nuestros últimos proyectos en Instagram.'}
          </p>
          <div className="mt-10">
            <a
              href="https://www.instagram.com/galene_home/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-link"
            >
              <Button className="bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-10 py-6 text-lg">
                @galene_home
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#8DA399]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            {language === 'en'
              ? 'Let\'s Create Your Serene Space'
              : 'Creemos Tu Espacio Sereno'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/80 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Ready to transform your home into a sanctuary of calm? We\'re here to help.'
              : '¿Lista para transformar tu hogar en un santuario de calma? Estamos aquí para ayudarte.'}
          </p>
          <div className="mt-10">
            <Link to="/contact">
              <Button
                data-testid="about-cta-button"
                className="bg-white text-[#8DA399] hover:bg-[#F2F0E9] rounded-full px-10 py-6 text-lg shadow-lg"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
